import { checkNever } from '../../../utils/checkNever';
import { IReference, ReferenceType } from '../references/IReference';
import {
  IReferencePathItem,
  ReferencePathDirection,
} from '../references/IReferencePath';

/**
 * Converte un riferimento in un percorso valido per il `parentWhen` di yup.
 * Questi percorsi sono _relativi_
 */
export function mapReferenceToPath(ref: IReference): string {
  switch (ref.type) {
    case ReferenceType.Sample:
      return `$sample.${ref.code}`;
    case ReferenceType.Register:
      return `$registers.${ref.code}`;
    case ReferenceType.Widget:
      return createWidgetReferencePath(ref);
    case ReferenceType.Session:
      return mapSessionReferenceToPath(ref);
    case ReferenceType.VirtualColumn:
      return `$virtualColumns.${ref.code}`;
    case ReferenceType.SemanticFields:
      return `$semanticFields.${ref.code}`;
    case ReferenceType.DossierSemanticFields:
      return `$dossierSemanticFields.${ref.code}`;
    case ReferenceType.Metadata:
      return mapMetadataReferenceToPath(ref);
    default:
      console.error(`[MapReferenceToPath] Unsupported reference type`, {ref}); // prettier-ignore
      checkNever(ref.type);
      return '<unknown>';
  }
}

function createWidgetReferencePath(reference: IReference) {
  return [...reference.path.map(parseWidgetPath), reference.code]
    .filter(p => p !== null)
    .join('.');
}

export function mapSessionReferenceToPath(ref: IReference): string {
  const [rootPath, ...otherPaths] = ref.path ?? [];
  if (
    rootPath?.direction === ReferencePathDirection.Absolute &&
    rootPath?.code === 'session' &&
    otherPaths.length > 0
  ) {
    const pathTokens = otherPaths.map(p => p.code).join('.');
    return `$session.${pathTokens}.${ref.code}`;
  }

  return `$session.${ref.code}`;
}

function parseWidgetPath(path: IReferencePathItem) {
  if (path.direction === ReferencePathDirection.Parent) {
    return `^`;
  }

  if (path.direction === ReferencePathDirection.Root) {
    return `^.${path.code}`;
  }

  return path.code;
}

/**
 * Il percorso è composto dal nodo radice `$metadata` e da eventuali nodi figli
 * specificati nel path (es. `$metadata.submission.richiestaInIntegrazione`).
 */
function mapMetadataReferenceToPath(ref: IReference): string {
  const [rootPath, ...otherPaths] = ref.path ?? [];
  if (
    rootPath?.direction === ReferencePathDirection.Absolute &&
    rootPath?.code === 'metadata' &&
    otherPaths.length > 0
  ) {
    const pathTokens = otherPaths.map(p => p.code).join('.');
    return `$metadata.${pathTokens}.${ref.code}`;
  }

  return `$metadata.${ref.code}`;
}
